import { A11yModule } from '@angular/cdk/a11y';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMediaThumbnailModule } from '@go-modules/components/media-thumbnail/media-thumbnail.module';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxRichTextEditorModule } from '@go-modules/components/rich-text-editor';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { TranslateModule } from '@ngx-translate/core';
import { mediaPreviewToken } from 'go-modules/modals/media-preview/modal.factory';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import {
	LibraryDialogComponent,
	type LibraryDialogContext
} from '../library-dialog/library-dialog.component';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { filter, tap } from 'rxjs';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { MediaSchema } from 'go-modules/models/media';
import { NgxRichTextEditorComponent } from '@go-modules/components/rich-text-editor/rich-text-editor.component';
import { UniqueIdGeneratorUtil } from '@go-modules/utilities/unique-id-generator/unique-id-generator.util';
import { GoDialogContentDirective } from '@go-modules/directives/go-dialog-content/go-dialog-content.directive';

export interface TaskInstructionsDialogData {
	text: string;
	media: MediaSchema | null;
	groupId?: number | null;
	context?: LibraryDialogContext;
}

@Component({
	selector: 'task-instructions-dialog',
	templateUrl: './task-instructions-dialog.component.html',
	styleUrls: ['./task-instructions-dialog.component.scss'],
	imports: [
		TranslateModule,
		MatDialogModule,
		MatTooltipModule,
		NgxRichTextEditorModule,
		PanelModule,
		FormsModule,
		ReactiveFormsModule,
		NgxMediaThumbnailModule,
		A11yModule,
		GoDialogContentDirective
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class TaskInstructionsDialogComponent implements OnInit {
	public dialogTitleId = `task-instructions-dialog-title-${UniqueIdGeneratorUtil.getId()}`;
	public readonly CHARACTER_LIMIT = 20000;
	public form: FormGroup<{ text: FormControl<string> }>;
	public editedData: {
		text: string,
		media: MediaSchema|null
	};

	private initialMediaId: string|number|null = null;
	private initialText: string = '';

	@ViewChild('instructionsEditor') private instructionsEditor: NgxRichTextEditorComponent;

	constructor (
		public dialogRef: GoDialogRef,
		private cdr: ChangeDetectorRef,
		private ngxGoModalService: GoModalService,
		@Inject(mediaToken) private MediaModel,
		@Inject(mediaPreviewToken) private mediaPreviewModal,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(GO_MODAL_DATA) public data: TaskInstructionsDialogData
	) {
		this.editedData = structuredClone({
			text: this.data.text,
			media: this.data.media
		});
	}

	public ngOnInit () {
		this.form = new FormGroup({
			text: new FormControl<string>(this.editedData.text ?? '')
		});
		this.editedData.media = this.editedData.media ? this.MediaModel.model(this.editedData.media) : null;
		this.initialMediaId = this.editedData.media?.media_id ?? null;
		this.initialText = this.editedData.text ?? '';
	}

	public onFormSubmit () {
		if (this.instructionText().length > this.CHARACTER_LIMIT) {
			this.instructionsEditor.focusOnField();
			return;
		}

		this.dialogRef.close({
			text: this.form.controls.text.value,
			media: this.editedData.media
		});
	}

	public openMediaPreviewModal (edit: boolean) {
		const modalData = {
			media: this.editedData.media,
			edit,
			allowDownload: true,
			groupId: this.resolveGroupId()
		};

		return this.mediaPreviewModal.open({ modalData }).result.then((result) => {
			this.editedData.media = this.MediaModel.model(result);
			this.cdr.markForCheck();
		});
	}

	public openRemoveMediaInstructionsModal () {
		this.editedData.media = null;
		this.markFormDirtyIfChanged();
	}

	public updateText (updatedText: string) {
		this.editedData.text = updatedText;
		this.form.controls.text.setValue(this.editedData.text);

		this.markFormDirtyIfChanged();
	}

	public instructionText (): string {
		return this.editedData.text ?? '';
	}

	public openLibrary () {
		return this.ngxGoModalService.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.MEDIA, CONTENT_TYPES.DOCUMENTS],
				mode: MODES.SELECT,
				previewable: false,
				collectionManagement: true,
				context: this.data.context
			}
		}).afterClosed()
			.pipe(
				filter((confirm) => confirm),
				tap((res) => {
					this.editedData.media = this.MediaModel.model(res);
					this.cdr.markForCheck();
					this.markFormDirtyIfChanged();
				})
			)
			.subscribe();
	}

	public dismiss () {
		this.dialogRef.close();
	}

	private markFormDirtyIfChanged () {
		const mediaChanged: boolean = (this.editedData.media?.media_id ?? null) !== this.initialMediaId;
		const hasEditedText: boolean = this.editedData.text !== this.initialText;

		if (mediaChanged || hasEditedText) {
			this.form.markAsDirty();
		} else {
			this.form.markAsPristine();
		}
	}

	private resolveGroupId (): number | null {
		if (this.data.groupId !== undefined) {
			return this.data.groupId;
		}

		return this.selectedService.getGroup()?.group_id ?? null;
	}
}
